import { ILogger } from '@remento/logger';
import { EventEmitter } from '@remento/utils/event/EventEmitter';
import { waitForPromise, waitForProperty } from '@remento/web-utils/dom/initialization';

import { ConsentRepository, ConsentRepositoryEvents, ConsentState } from './consent-repository.js';

interface TermlyConsentState {
  unclassified: boolean;
  essential: boolean;
  performance: boolean;
  analytics: boolean;
  advertising: boolean;
  social_networking: boolean;
}

interface TermlyAPI {
  getConsentState(): TermlyConsentState;
  on: (event: 'initialized' | 'consent', callback: () => void) => void;
}

declare global {
  interface Window {
    Termly: TermlyAPI;
  }
}

export class TermlyConsentRepository extends EventEmitter<ConsentRepositoryEvents> implements ConsentRepository {
  private init: Promise<TermlyAPI | null>;

  constructor(private logger: ILogger) {
    super();
    // Wait for Termly to load and register an event observer
    this.init = waitForPromise(waitForProperty(window, 'Termly'), 5000)
      .then(() => {
        if (typeof window.Termly.getConsentState == 'function') {
          return window.Termly;
        }
        return new Promise<void>((resolve) => window.Termly.on('initialized', resolve)).then(() => window.Termly);
      })
      .then((termly) => {
        const consent = this.mapConsent(window.Termly.getConsentState());
        this.logger.debug('TERMLY_INIT', () => ({ ...consent }));

        termly.on('consent', () => {
          const consent = this.mapConsent(termly.getConsentState());
          this.logger.debug('TERMLY_UPDATE', () => ({ ...consent }));
          this.emit('change', consent);
        });
        return termly;
      })
      .catch((err) => {
        logger.error('TERMLY_INIT_ERROR', () => ({ error: err }));
        return null;
      });
  }

  private mapConsent(consent: TermlyConsentState | undefined): ConsentState {
    if (consent == null) {
      return {
        unclassified: false,
        essential: false,
        performance: false,
        analytics: false,
        advertising: false,
        socialNetworking: false,
      };
    }
    const { social_networking, ...state } = consent;
    return {
      ...state,
      socialNetworking: social_networking,
    };
  }

  async getConsentState(): Promise<ConsentState> {
    const termly = await this.init;
    return this.mapConsent(termly?.getConsentState());
  }
}
